import { Schema } from "effect";
import { EnvironmentIdSchema, ProjectIdSchema, UserIdSchema, WorkspaceIdSchema } from "./ids";
import { IanaTimeZoneSchema, IsoDateTimeSchema, PapersFlowBoundaryParseOptions } from "./standard";
import { utf8ByteLength } from "./utf8";

const WorkspaceNameSchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(120));

export const PROJECT_NAME_MAX_LENGTH = 80;
export const PROJECT_DESCRIPTION_MAX_LENGTH = 500;
export const PROJECT_INSTRUCTIONS_MAX_BYTES = 8 * 1_024;

export const ProjectNameSchema = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(PROJECT_NAME_MAX_LENGTH),
);
export const ProjectDescriptionSchema = Schema.Trim.check(
  Schema.isMaxLength(PROJECT_DESCRIPTION_MAX_LENGTH),
);
export const ProjectInstructionsSchema = Schema.Trim.check(
  Schema.isMaxLength(PROJECT_INSTRUCTIONS_MAX_BYTES),
  Schema.makeFilter(
    (value) =>
      utf8ByteLength(value) <= PROJECT_INSTRUCTIONS_MAX_BYTES ||
      `must be at most ${PROJECT_INSTRUCTIONS_MAX_BYTES} UTF-8 bytes`,
  ),
);

export const WorkspaceKindSchema = Schema.Literals(["personal", "organization"]);
export const WorkspaceRoleSchema = Schema.Literals([
  "owner",
  "admin",
  "developer",
  "member",
  "billing",
  "guest",
]);

export const PermissionSchema = Schema.Literals([
  "workspace.read",
  "workspace.manage",
  "project.read",
  "project.manage",
  "thread.read",
  "thread.write",
  "task.write",
  "agent.run",
  "agent.configure",
  "connection.use",
  "connection.manage",
  "calendar.read",
  "calendar.write",
  "calendar.publish",
  "apiKey.read",
  "apiKey.create",
  "apiKey.revoke",
  "member.invite",
  "member.remove",
  "audit.read",
  "billing.read",
  "billing.manage",
]);

export const WORKSPACE_ROLE_PERMISSIONS = {
  owner: [
    "workspace.read",
    "workspace.manage",
    "project.read",
    "project.manage",
    "thread.read",
    "thread.write",
    "task.write",
    "agent.run",
    "agent.configure",
    "connection.use",
    "connection.manage",
    "calendar.read",
    "calendar.write",
    "calendar.publish",
    "apiKey.read",
    "apiKey.create",
    "apiKey.revoke",
    "member.invite",
    "member.remove",
    "audit.read",
    "billing.read",
    "billing.manage",
  ],
  admin: [
    "workspace.read",
    "workspace.manage",
    "project.read",
    "project.manage",
    "thread.read",
    "thread.write",
    "task.write",
    "agent.run",
    "agent.configure",
    "connection.use",
    "connection.manage",
    "calendar.read",
    "calendar.write",
    "calendar.publish",
    "apiKey.read",
    "apiKey.create",
    "apiKey.revoke",
    "member.invite",
    "member.remove",
    "audit.read",
    "billing.read",
  ],
  developer: [
    "workspace.read",
    "project.read",
    "project.manage",
    "thread.read",
    "thread.write",
    "task.write",
    "agent.run",
    "connection.use",
    "calendar.read",
    "calendar.write",
    "calendar.publish",
    "apiKey.read",
    "apiKey.create",
    "apiKey.revoke",
  ],
  member: [
    "workspace.read",
    "project.read",
    "thread.read",
    "thread.write",
    "task.write",
    "agent.run",
    "connection.use",
    "calendar.read",
    "calendar.write",
    "calendar.publish",
  ],
  billing: ["workspace.read", "billing.read", "billing.manage"],
  guest: ["workspace.read", "project.read", "thread.read", "calendar.read"],
} as const satisfies Record<WorkspaceRole, readonly Permission[]>;

export const ActorSchema = Schema.Struct({
  userId: UserIdSchema,
  workspaceId: WorkspaceIdSchema,
  role: WorkspaceRoleSchema,
  permissions: Schema.Array(PermissionSchema).check(Schema.isMaxLength(64)),
});

export const WorkspaceSchema = Schema.Struct({
  id: WorkspaceIdSchema,
  kind: WorkspaceKindSchema,
  name: WorkspaceNameSchema,
  timeZone: Schema.optionalKey(IanaTimeZoneSchema),
  ownerUserId: Schema.optionalKey(UserIdSchema),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});

export const ProjectSchema = Schema.Struct({
  id: ProjectIdSchema,
  workspaceId: WorkspaceIdSchema,
  name: ProjectNameSchema,
  slug: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
  description: Schema.optionalKey(ProjectDescriptionSchema),
  instructions: Schema.optionalKey(ProjectInstructionsSchema),
  visibility: Schema.Literals(["restricted", "workspace"]),
  status: Schema.Literals(["active", "archived", "deleting"]),
  createdBy: UserIdSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  archivedAt: Schema.optionalKey(IsoDateTimeSchema),
});

export const EnvironmentSchema = Schema.Struct({
  id: EnvironmentIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  name: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
  kind: Schema.Literals(["development", "preview", "production", "custom"]),
});

export const decodeActor = Schema.decodeUnknownSync(ActorSchema, PapersFlowBoundaryParseOptions);
export const encodeActor = Schema.encodeSync(ActorSchema, PapersFlowBoundaryParseOptions);
export const decodeWorkspace = Schema.decodeUnknownSync(
  WorkspaceSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeProject = Schema.decodeUnknownSync(
  ProjectSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeProject = Schema.encodeSync(ProjectSchema, PapersFlowBoundaryParseOptions);
export const decodeEnvironment = Schema.decodeUnknownSync(
  EnvironmentSchema,
  PapersFlowBoundaryParseOptions,
);

export type WorkspaceKind = typeof WorkspaceKindSchema.Type;
export type WorkspaceRole = typeof WorkspaceRoleSchema.Type;
export type Permission = typeof PermissionSchema.Type;
export type Actor = typeof ActorSchema.Type;
export type ActorEncoded = typeof ActorSchema.Encoded;
export type Workspace = typeof WorkspaceSchema.Type;
export type WorkspaceEncoded = typeof WorkspaceSchema.Encoded;
export type Project = typeof ProjectSchema.Type;
export type ProjectEncoded = typeof ProjectSchema.Encoded;
export type Environment = typeof EnvironmentSchema.Type;
export type EnvironmentEncoded = typeof EnvironmentSchema.Encoded;
